import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mail, Send, LogOut, TrendingUp, Users, DollarSign, Percent, Download, Trash2 } from 'lucide-react';
import { referrer as referrerApi } from '@/lib/api';
import { toast } from 'sonner';

const money = (v) => `A$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ReferrerLoginPage() {
  const [email, setEmail] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (referrerApi.getToken()) navigate('/referrer/dashboard', { replace: true });
  }, [navigate]);

  React.useEffect(() => {
    // Consume magic-link token from ?token=
    const token = params.get('token');
    if (!token) return;
    referrerApi.consume(token).then((r) => {
      referrerApi.setToken(r.session_token);
      toast.success(`Welcome, ${r.contact.name}`);
      navigate('/referrer/dashboard', { replace: true });
    }).catch((e) => toast.error(e?.response?.data?.detail || 'Invalid or expired link'));
  }, [params, navigate]);

  const submit = async () => {
    if (!email.trim()) { toast.error('Enter your email'); return; }
    setSending(true);
    try { await referrerApi.requestLogin(email.trim()); setSent(true); }
    catch { toast.error('Something went wrong'); }
    finally { setSending(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-8" data-testid="referrer-login-page">
      <Card className="w-full max-w-md">
        <CardContent className="p-10">
          <div className="mb-6 text-center">
            <div className="mb-2 font-serif text-2xl font-bold text-[#1A3A5C]">ImmiPro Referrer Portal</div>
            <p className="text-sm text-slate-500">See your referred pipeline, revenue and commission split.</p>
          </div>
          {sent ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center text-sm text-emerald-800">
              <Send className="mx-auto mb-2 h-6 w-6" />
              If <span className="font-semibold">{email}</span> is a registered referrer, a sign-in link is on its way.
              <p className="mt-2 text-xs">Click the link in your inbox to continue.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourfirm.com" data-testid="referrer-login-email" />
              </div>
              <Button className="w-full" onClick={submit} disabled={sending} data-testid="referrer-login-submit">
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}Email me a sign-in link
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function ReferrerDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!referrerApi.getToken()) { navigate('/referrer/login', { replace: true }); return; }
    referrerApi.dashboard()
      .then(setData)
      .catch((e) => {
        if (e?.response?.status === 401) { referrerApi.clearToken(); navigate('/referrer/login', { replace: true }); }
        else toast.error('Failed to load dashboard');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const signOut = () => { referrerApi.clearToken(); navigate('/referrer/login', { replace: true }); };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-[#2ECC9A]" /></div>;
  if (!data) return null;
  const s = data.summary;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="referrer-dashboard">
      <header className="border-b border-slate-200 bg-white px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div>
            <div className="font-serif text-xl font-bold text-[#1A3A5C]">Referrer portal</div>
            <div className="text-xs text-slate-500">Welcome, {data.contact.name}{data.contact.org_name ? ` · ${data.contact.org_name}` : ''}</div>
          </div>
          <Button variant="outline" size="sm" onClick={signOut} data-testid="referrer-signout"><LogOut className="mr-2 h-4 w-4" /> Sign out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl p-8">
        <div className="grid gap-4 md:grid-cols-4">
          <Kpi icon={Users} label="Matters referred" value={s.matter_count} sub={`${s.buckets.active + s.buckets.intake} active · ${s.buckets.decided} decided`} testid="referrer-kpi-matters" />
          <Kpi icon={DollarSign} label="Lifetime revenue" value={money(s.lifetime_revenue)} sub={`${money(s.pending_revenue)} pending`} testid="referrer-kpi-revenue" />
          <Kpi icon={Percent} label="Commission split" value={`${s.commission_pct}%`} sub="Configurable per referrer" testid="referrer-kpi-commission-pct" />
          <Kpi icon={TrendingUp} label="Commission earned" value={money(s.commission_earned)} sub="Paid revenue × split" testid="referrer-kpi-commission-value" />
        </div>

        <Card className="mt-6"><CardContent className="p-6">
          <div className="mb-3 font-serif text-lg font-semibold text-[#1A3A5C]">Your referrals</div>
          {data.matters.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">No matters yet. Referrals your firm attributes to you will appear here.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2">Matter</th><th className="px-3 py-2">Applicant</th><th className="px-3 py-2">Subclass</th><th className="px-3 py-2">Stage</th><th className="px-3 py-2">Referred</th></tr>
              </thead>
              <tbody>
                {data.matters.map((m) => (
                  <tr key={m.id} className="border-b border-slate-50" data-testid={`referrer-matter-${m.id}`}>
                    <td className="px-3 py-2 font-semibold text-[#2ECC9A]">{m.ref}</td>
                    <td className="px-3 py-2 text-slate-700">{typeof m.applicant === 'string' ? m.applicant : m.applicant?.name || m.applicant_name || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{m.subclass || '—'}</td>
                    <td className="px-3 py-2"><Badge className="border border-slate-200 bg-slate-50 text-slate-700">{m.stage || '—'}</Badge></td>
                    <td className="px-3 py-2 text-xs text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleDateString('en-AU') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent></Card>

        {data.invoices.length > 0 && (
          <Card className="mt-6"><CardContent className="p-6">
            <div className="mb-3 font-serif text-lg font-semibold text-[#1A3A5C]">Invoice history</div>
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2">Matter</th><th className="px-3 py-2">Description</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Date</th></tr>
              </thead>
              <tbody>
                {data.invoices.slice(0, 20).map((i) => (
                  <tr key={i.id} className="border-b border-slate-50" data-testid={`referrer-invoice-${i.id}`}>
                    <td className="px-3 py-2 font-semibold text-[#2ECC9A]">{i.matter_ref}</td>
                    <td className="px-3 py-2 text-slate-700">{i.description || '—'}</td>
                    <td className="px-3 py-2 font-semibold text-[#1A3A5C]">{money(i.amount)}</td>
                    <td className="px-3 py-2"><Badge className={`border ${i.status === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>{i.status}</Badge></td>
                    <td className="px-3 py-2 text-xs text-slate-500">{(i.paid_at || i.created_at || '').slice(0, 10) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        )}
        <ReferrerDocVault />
      </main>
    </div>
  );
}

function ReferrerDocVault() {
  const [docs, setDocs] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [label, setLabel] = React.useState('Lead brief');
  const [leadName, setLeadName] = React.useState('');
  const inputRef = React.useRef(null);
  const load = () => {
    const t = referrerApi.getToken();
    fetch(`${(process.env.REACT_APP_BACKEND_URL || '') + '/api'}/referrer/docs`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.json()).then((d) => setDocs(d.items || [])).catch(() => {});
  };
  React.useEffect(load, []);
  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (label) fd.append('label', label);
    if (leadName) fd.append('lead_name', leadName);
    try {
      const t = referrerApi.getToken();
      const r = await fetch(`${(process.env.REACT_APP_BACKEND_URL || '') + '/api'}/referrer/docs`, {
        method: 'POST', headers: { Authorization: `Bearer ${t}` }, body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Upload failed');
      toast.success(`${file.name} uploaded`); load();
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  return (
    <Card className="mt-6" data-testid="referrer-doc-vault"><CardContent className="p-6">
      <div className="mb-3 flex items-center justify-between">
        <div className="font-serif text-lg font-semibold text-[#1A3A5C]">Lead qualification vault</div>
        <div className="text-xs text-slate-500">{docs.length} file{docs.length === 1 ? '' : 's'}</div>
      </div>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]"><Input value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Lead name (optional)" data-testid="referrer-doc-lead" /></div>
        <div className="flex-1 min-w-[180px]"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" data-testid="referrer-doc-label" /></div>
        <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="referrer-doc-upload">
          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Upload
        </Button>
        <input ref={inputRef} type="file" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
      </div>
      {docs.length === 0 ? <div className="text-center text-xs text-slate-400 py-4">No files uploaded yet.</div>
       : <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3 text-sm" data-testid={`referrer-doc-${d.id}`}>
              <div>
                <div className="font-medium text-[#1A3A5C]">{d.file_name}</div>
                <div className="text-xs text-slate-500">
                  {d.lead_name && `Lead: ${d.lead_name} · `}
                  {d.label && `${d.label} · `}
                  {(d.size_bytes / 1024).toFixed(1)} KB · {new Date(d.uploaded_at).toLocaleString('en-AU')}
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" data-testid={`referrer-doc-download-${d.id}`}
                  onClick={async () => {
                    try {
                      const t = referrerApi.getToken();
                      const r = await fetch(`${(process.env.REACT_APP_BACKEND_URL || '') + '/api'}/referrer/docs/${d.id}/download`, { headers: { Authorization: `Bearer ${t}` } });
                      if (!r.ok) throw new Error('Download failed');
                      const blob = await r.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = d.file_name; a.click();
                      setTimeout(() => URL.revokeObjectURL(url), 5000);
                    } catch { toast.error('Download failed'); }
                  }}><Download className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" data-testid={`referrer-doc-delete-${d.id}`}
                  onClick={async () => {
                    if (!window.confirm(`Delete ${d.file_name}?`)) return;
                    try {
                      const t = referrerApi.getToken();
                      const r = await fetch(`${(process.env.REACT_APP_BACKEND_URL || '') + '/api'}/referrer/docs/${d.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } });
                      if (!r.ok && r.status !== 204) throw new Error('Delete failed');
                      toast.success('Deleted'); load();
                    } catch { toast.error('Delete failed'); }
                  }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      }
    </CardContent></Card>
  );
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card data-testid={testid}><CardContent className="p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Icon className="h-4 w-4" />{label}</div>
      <div className="mt-2 font-serif text-2xl font-bold text-[#1A3A5C]">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </CardContent></Card>
  );
}
